<template>
  <!-- A player for the beat's tracks and blends. Always on the home page,
       minimized and paused until you press play; elsewhere shown while the
       sound is on, playing or stopped, until its × (sound off) closes it.
       With ?player in the URL it shows everywhere (remembered; ?player=off
       forgets it). It sits in the presenter's bar, so it looks like part of
       the site. -->
  <teleport to="body">
    <!-- Reading pages: one round button, the chat button's size, in the
         other corner, and only when the beat was started from the home
         page's player in this visit. Bars moving to the beat while it plays, a play mark while
         it's paused; tapping it is the only control. Only while the sound
         is on; the footer's Sound switch turns it off. -->
    <button
      v-if="compact && state.sound && fromHome"
      type="button"
      :class="['beat-mini', { 'is-playing': state.playing }]"
      :title="state.playing ? 'Pause the beat' : 'Play the beat'"
      :aria-label="state.playing ? 'Pause the beat' : 'Play the beat'"
      :aria-pressed="state.playing ? 'true' : 'false'"
      @click="toggle"
    >
      <span v-if="state.playing" class="beat-mini__bars" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
      <svg v-else viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path :d="ICONS.play" />
      </svg>
    </button>
    <PresenterBar
      v-if="!compact && (enabled || state.sound || (isHome && !dismissed))"
      :class="['beat-player', { 'is-minimized': minimized }]"
      label="Beat player"
      role="region"
    >
      <MyButton
        type="ghost"
        size="xs"
        class="beat-player__icon"
        :label="state.playing ? 'Pause' : 'Play'"
        :title="state.playing ? 'Pause' : 'Play'"
        :aria-label="state.playing ? 'Pause the beat' : 'Play the beat'"
        @click="toggle"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" class="is-filled">
          <path :d="state.playing ? ICONS.pause : ICONS.play" />
        </svg>
      </MyButton>
      <!-- Minimized: just play/pause and what's playing, which opens it. -->
      <MyButton
        v-if="minimized"
        type="ghost"
        size="xs"
        class="beat-player__now"
        :label="currentLabel"
        title="Open the player"
        aria-label="Open the beat player"
        @click="setMinimized(false)"
      />
      <!-- One row at any width: the middle scrolls sideways and fades out at
           the edge rather than wrapping. Tracks first, then the modes. -->
      <div
        v-if="!minimized"
        ref="scroll"
        :class="['beat-player__scroll', { 'is-cut-off': cutOff }]"
        @scroll.passive="measure"
      >
        <MyButton
          v-for="t in state.tracks"
          :key="t.name"
          :type="t.name === state.current ? 'solid' : 'ghost'"
          size="xs"
          :class="['beat-player__track', { 'is-next': t.name === state.target }]"
          :label="t.label"
          :title="`${t.bpm} bpm`"
          :aria-label="`${state.blending ? 'Blend' : 'Cut'} to ${t.label}, ${t.bpm} bpm`"
          :aria-current="t.name === state.current ? 'true' : undefined"
          :disabled="!state.playing"
          @click="blend(t.name)"
        />
        <MyButton
          v-for="c in modes"
          :key="c.key"
          type="ghost"
          size="xs"
          class="beat-player__icon"
          :label="c.title"
          :title="c.title"
          :aria-label="c.aria"
          @click="c.action"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" :class="{ 'is-filled': c.filled }">
            <path :d="c.icon" />
          </svg>
        </MyButton>
      </div>
      <span v-if="!minimized" class="beat-player__rule" aria-hidden="true"></span>
      <MyButton
        v-if="!minimized"
        type="ghost"
        size="xs"
        class="beat-player__icon"
        label="Minimize"
        title="Minimize"
        aria-label="Minimize the beat player"
        @click="setMinimized(true)"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path :d="ICONS.minimize" />
        </svg>
      </MyButton>
      <!-- × turns the sound off and closes the player. On the home page it's
           there minimized too, and closes the player for the rest of the
           visit. -->
      <MyButton
        v-if="(state.sound && !minimized) || isHome"
        type="ghost"
        size="xs"
        class="beat-player__icon"
        label="Sound off"
        title="Sound off"
        aria-label="Turn sound off and close the player"
        @click="close"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path :d="ICONS.close" />
        </svg>
      </MyButton>
    </PresenterBar>
  </teleport>
</template>

<script>
import MyButton from '@/components/Button/Button.vue';
import PresenterBar from '@/components/PresenterBar/PresenterBar.vue';
import {
  getBeatState,
  startBeat,
  pauseBeat,
  setSound,
  blendTo,
  setAuto,
  setBlend,
  startedFromHome,
  markStartedFromHome,
} from '@/utils/chillBeat';

// The icons, on a 24 grid. Filled ones (play, pause) are shapes; the
// rest are 2px strokes, like the site's other icons.
const ICONS = {
  play: 'M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z',
  pause: 'M7 5h3a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm7 0h3a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
  // Two lines crossing over each other: a crossfade.
  blend: 'M3 7h3c5 0 7 10 12 10h3M3 17h3c5 0 7-10 12-10h3',
  // A straight jump to a bar: a cut.
  cut: 'M3 12h13M12 7l5 5-5 5M20 5v14',
  // Arrows going round: carry on through the tracks.
  auto: 'M4 11V9a4 4 0 0 1 4-4h11M16 2l3 3-3 3M20 13v2a4 4 0 0 1-4 4H5M8 22l-3-3 3-3',
  // A padlock: stay on this one.
  hold: 'M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5Z',
  minimize: 'M6 12h12',
  close: 'M5 5l14 14M19 5L5 19',
};

export default {
  name: 'BeatPlayer',
  components: { MyButton, PresenterBar },
  data() {
    return {
      ICONS,
      enabled: false,
      minimized: false,
      cutOff: false,
      state: getBeatState(),
      fromHome: startedFromHome(),
      dismissed: (() => {
        try {
          return sessionStorage.getItem('beat-player-closed') === 'on';
        } catch {
          return false;
        }
      })(),
    };
  },
  computed: {
    isHome() {
      return this.$route?.path === '/';
    },
    // Off the home page the focus is reading and the chat, so the player is
    // the one square, unless ?player asked for the full bar everywhere.
    compact() {
      return !this.isHome && !this.enabled;
    },
    // The mode buttons, each showing the mode it's in.
    modes() {
      const { blending, auto } = this.state;
      return [
        {
          key: 'blend',
          icon: blending ? ICONS.blend : ICONS.cut,
          title: blending ? 'Blend: tracks crossfade' : 'Cut: tracks switch at once',
          aria: blending ? 'Blending; switch to cutting' : 'Cutting; switch to blending',
          action: this.toggleBlend,
        },
        {
          key: 'auto',
          icon: auto ? ICONS.auto : ICONS.hold,
          title: auto ? 'Auto: moves through the tracks' : 'Hold: stays on this track',
          aria: auto ? 'Moving through the tracks; hold this one' : 'Holding this track; move through them',
          action: this.toggleAuto,
        },
      ];
    },
    currentLabel() {
      // Before anything plays, the track it will open with (always the first).
      const t =
        this.state.tracks.find((x) => x.name === this.state.current) || this.state.tracks[0];
      return t ? t.label : 'Beat';
    },
  },
  mounted() {
    const param = new URLSearchParams(window.location.search).get('player');
    try {
      if (param === 'off') localStorage.removeItem('beat-player');
      else if (param !== null) localStorage.setItem('beat-player', 'on');
      this.enabled = localStorage.getItem('beat-player') === 'on';
    } catch {
      this.enabled = param !== null && param !== 'off';
    }
    // Minimized unless you've opened it before.
    try {
      this.minimized = localStorage.getItem('beat-player-min') !== 'off';
    } catch {
      this.minimized = true;
    }
    window.addEventListener('chillbeat', this.refresh);
    window.addEventListener('chillbeat-state', this.refresh);
    window.addEventListener('resize', this.measure, { passive: true });
  },
  updated() {
    this.measure();
  },
  beforeUnmount() {
    window.removeEventListener('chillbeat', this.refresh);
    window.removeEventListener('chillbeat-state', this.refresh);
    window.removeEventListener('resize', this.measure);
  },
  methods: {
    // Whether the middle runs past its edge (and isn't scrolled to the end),
    // which is when it fades out.
    measure() {
      const el = this.$refs.scroll;
      const cut = !!el && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      if (cut !== this.cutOff) this.cutOff = cut;
    },
    refresh() {
      this.state = getBeatState();
    },
    // Stop pauses the music and keeps the player; Play starts it, turning
    // the sound on first if it was off (from ?player).
    toggle() {
      if (!this.state.playing && this.isHome) {
        markStartedFromHome();
        this.fromHome = true;
      }
      if (this.state.playing) pauseBeat();
      else if (this.state.sound) startBeat();
      else setSound(true);
      this.refresh();
    },
    // × is the sound switch: off, and the player goes with it.
    close() {
      setSound(false);
      if (this.isHome) {
        this.dismissed = true;
        try {
          sessionStorage.setItem('beat-player-closed', 'on');
        } catch {
          // ignore: it comes back on the next load
        }
      }
      this.refresh();
    },
    blend(name) {
      blendTo(name);
    },
    toggleAuto() {
      setAuto(!this.state.auto);
    },
    // Remembered, so it stays small across pages and visits.
    setMinimized(on) {
      this.minimized = on;
      try {
        localStorage.setItem('beat-player-min', on ? 'on' : 'off');
      } catch {
        // ignore
      }
    },
    toggleBlend() {
      setBlend(!this.state.blending);
    },
  },
};
</script>

<style lang="scss" scoped>
/* A round button, the chat button's size and border, mirrored to the bottom
   left; round like the full player's ends, so it reads as the player. */
.beat-mini {
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 9998;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  inline-size: 54px;
  block-size: 54px;
  margin: 0;
  padding: 0;
  border: var(--border);
  border-width: 2px; /* design-guard:ignore */
  border-radius: 50%; /* design-guard:ignore */
  background: var(--background);
  color: var(--foreground);
  cursor: pointer;
  svg {
    display: block;
    fill: currentColor;
  }
  &:focus-visible {
    outline: 2px solid var(--foreground); /* design-guard:ignore */
    outline-offset: 2px; /* design-guard:ignore */
  }
}
/* Three bars that bounce on the beat (--beat is one beat, set while it plays),
   each a little out of step with the next. */
.beat-mini__bars {
  display: flex;
  align-items: flex-end;
  gap: 3px; /* design-guard:ignore */
  block-size: 18px; /* design-guard:ignore */
  span {
    inline-size: 4px; /* design-guard:ignore */
    block-size: 100%;
    border-radius: 1px; /* design-guard:ignore */
    background: currentColor;
    transform-origin: bottom;
    animation: beat-mini-bar var(--beat, 0.7s) ease-in-out infinite alternate;
  }
  span:nth-child(2) {
    animation-delay: calc(var(--beat, 0.7s) * -0.33);
  }
  span:nth-child(3) {
    animation-delay: calc(var(--beat, 0.7s) * -0.66);
  }
}
@keyframes beat-mini-bar {
  from {
    transform: scaleY(0.3);
  }
  to {
    transform: scaleY(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .beat-mini__bars span {
    animation: none;
    transform: scaleY(0.7);
  }
}

/* Bottom left, mirroring the chat button at the bottom right: the same
   20px from the bottom and the side, and the same 54px tall. Never wide
   enough to run under it, and when it reaches it, the same 20px between. */
.beat-player {
  flex-wrap: nowrap;
  box-sizing: border-box;
  left: 20px;
  bottom: 20px;
  block-size: 54px;
  /* Tighter than the presenter bar it borrows: half the padding at the ends
     and half the gap between controls, so the bar carries the controls rather
     than the space around them. */
  padding-block: 0;
  padding-inline: var(--spacing-xxs);
  gap: var(--spacing-xxxs);
  transform: none;
  max-inline-size: calc(100vw - 40px - 54px - 20px);
}
/* Phones: minimized, it's only as wide as play and the track name. Open, it
   takes the width of the screen, less the margins, while the chat button is
   away (the home page with the avatar up); when the button slides in, the bar
   narrows to leave its room, in step with it. */
@media (max-width: 767px) {
  .beat-player:not(.is-minimized) {
    inline-size: calc(100vw - 40px - 54px - 20px);
    max-inline-size: none;
    transition: inline-size 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  :root.is-home.home-avatar-in:not(.menu-open) .beat-player:not(.is-minimized) {
    inline-size: calc(100vw - 40px);
  }
}
@media (max-width: 767px) and (prefers-reduced-motion: reduce) {
  .beat-player:not(.is-minimized) {
    transition: none;
  }
}
.beat-player__scroll {
  display: flex;
  align-items: center;
  gap: var(--spacing-xxxs);
  flex: 1 1 auto;
  min-inline-size: 0;
  overflow-x: auto;
  scrollbar-width: none;
  /* When it runs past the edge, the last bit fades out, so a cut-off track
     reads as "more this way". */
  &.is-cut-off {
    mask-image: linear-gradient(to right, #000 calc(100% - 2rem), transparent);
  }
  &::-webkit-scrollbar {
    display: none;
  }
  > * {
    flex: none;
  }
}
/* Icon buttons: round, with a slim pad around the 24px icon, so the fill
   they take on hover or after a tap is a circle like the bar's ends. */
.beat-player__icon {
  flex: none;
  :deep(.custom-btn),
  &:deep(.custom-btn) {
    padding-inline: var(--spacing-xxxs);
    border-radius: 50%; /* design-guard:ignore */
  }
}
/* Icon buttons, all 24px so the bar is the same height open or minimized;
   flex: none so the button's label can't squeeze them. Strokes by default,
   shapes where marked. */
.beat-player__icon svg {
  display: block;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  &.is-filled {
    fill: currentColor;
    stroke: none;
  }
}
/* The track being blended into: underlined while it comes in. */
.beat-player__track.is-next {
  text-decoration: underline;
  text-underline-offset: 0.3em;
}
/* A hairline between the player's controls and its window controls. */
.beat-player__rule {
  flex: none;
  inline-size: 1px;
  block-size: 1.5rem;
  background: currentColor;
  opacity: 0.2;
}
</style>
